export interface HighlightEvent {
  imageUrl: string;
  eventUrl: string;
}

export interface UpcomingEvent {
  eventId: string;
  thumbnailUrl: string;
  eventName: string;
  date: string;
  location: string;
  eventUrl: string;
  imageUrl: string;
  /** Espelha `evento_destaque_peso` quando o backend passar a enviar o campo. */
  highlightWeight?: number;
  destaquePeso?: number;
  /** Relevancia 0-100 da listagem (70% faturamento pago + 30% visualizacoes). */
  catalogWeight?: number;
}

export interface MemoryPhoto {
  id: string;
  photoUrl: string;
  eventId: number;
  eventName: string;
  eventUrl: string;
  location: string;
  dateLabel: string;
  caption: string;
  width: number;
  height: number;
}

export interface RecentMemoriesResponse {
  photos: MemoryPhoto[];
  /** Total de fotos publicadas, nao o tamanho da faixa. */
  total: number;
  /** Quantos eventos ja tem album publicado. */
  events: number;
}

export interface HighlightEventsResponse {
  events: HighlightEvent[];
}

export interface UpcomingEventsResponse {
  events: UpcomingEvent[];
}

const LOCAL_API_ORIGIN = 'http://localhost:8080';

function resolveApiOrigin(): string {
  const configuredOrigin = String(import.meta.env.VITE_API_URL || '').trim().replace(/\/+$/, '');
  if (configuredOrigin !== '') {
    return configuredOrigin;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return LOCAL_API_ORIGIN;
    }
  }

  return '';
}

const API_ORIGIN = resolveApiOrigin();
const API_BASE_URL = API_ORIGIN !== '' ? `${API_ORIGIN}/api` : '';

function isLocalBrowserSession(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function rewriteAssetUrlForLocalDev(rawUrl: string): string {
  const trimmedUrl = String(rawUrl || '').trim();
  if (trimmedUrl === '' || !isLocalBrowserSession() || API_ORIGIN === '') {
    return trimmedUrl;
  }

  try {
    const assetUrl = new URL(trimmedUrl);
    if (assetUrl.origin !== API_ORIGIN || !assetUrl.pathname.startsWith('/uploads/')) {
      return trimmedUrl;
    }

    return `${window.location.origin}${assetUrl.pathname}${assetUrl.search}`;
  } catch {
    return trimmedUrl;
  }
}

let sharedUpcomingRequest: Promise<UpcomingEventsResponse> | null = null;

class ApiService {
  private async fetchWithMultipleStrategies<T>(endpoint: string): Promise<T> {
    if (API_BASE_URL === '') {
      throw new Error('API base URL is not configured. Define VITE_API_URL for this environment.');
    }

    const fullUrl = `${API_BASE_URL}${endpoint}`;

    const strategies = [
      {
        name: 'Standard CORS',
        config: {
          method: 'GET',
          mode: 'cors' as RequestMode,
          credentials: 'omit' as RequestCredentials,
          headers: {
            'Accept': 'application/json',
          },
        }
      },
      {
        name: 'No-CORS Mode',
        config: {
          method: 'GET',
          mode: 'no-cors' as RequestMode,
          credentials: 'omit' as RequestCredentials,
        }
      },
      {
        name: 'Same-Origin',
        config: {
          method: 'GET',
          mode: 'same-origin' as RequestMode,
          credentials: 'omit' as RequestCredentials,
          headers: {
            'Accept': 'application/json',
          },
        }
      },
      {
        name: 'Basic Fetch',
        config: {
          method: 'GET',
        }
      }
    ];

    for (const strategy of strategies) {
      try {
        const response = await Promise.race([
          fetch(fullUrl, strategy.config),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 8000)
          )
        ]);

        if (response.type === 'opaque') {
          throw new Error('Opaque response - trying next strategy');
        }

        if (!response.ok) {
          continue;
        }

        const data = await response.json();
        return data;

      } catch {
        continue;
      }
    }

    try {
      const jsonpResult = await this.tryJsonpApproach(endpoint);
      if (jsonpResult) {
        return jsonpResult as T;
      }
    } catch {}

    throw new Error(`All API strategies failed for ${fullUrl}.`);
  }

  private async tryJsonpApproach<T>(endpoint: string): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const callbackName = `jsonp_callback_${Date.now()}`;
      const script = document.createElement('script');

      (window as any)[callbackName] = (data: T) => {
        delete (window as any)[callbackName];
        document.head.removeChild(script);
        resolve(data);
      };

      script.onerror = () => {
        delete (window as any)[callbackName];
        document.head.removeChild(script);
        reject(new Error('JSONP request failed'));
      };

      script.src = `${API_BASE_URL}${endpoint}&callback=${callbackName}`;
      document.head.appendChild(script);

      setTimeout(() => {
        if ((window as any)[callbackName]) {
          delete (window as any)[callbackName];
          document.head.removeChild(script);
          reject(new Error('JSONP timeout'));
        }
      }, 10000);
    });
  }

  async getHighlightEvents(): Promise<HighlightEventsResponse> {
    const response = await this.fetchWithMultipleStrategies<HighlightEventsResponse>('/events?type=highlights');
    return {
      ...response,
      events: Array.isArray(response.events)
        ? response.events.map((event) => ({
            ...event,
            imageUrl: rewriteAssetUrlForLocalDev(event.imageUrl),
          }))
        : [],
    };
  }

  async getUpcomingEvents(): Promise<UpcomingEventsResponse> {
    const response = await this.fetchWithMultipleStrategies<UpcomingEventsResponse>('/events?type=upcoming');
    return {
      ...response,
      events: Array.isArray(response.events)
        ? response.events.map((event) => ({
            ...event,
            imageUrl: rewriteAssetUrlForLocalDev(event.imageUrl),
            thumbnailUrl: rewriteAssetUrlForLocalDev(event.thumbnailUrl),
          }))
        : [],
    };
  }

  /**
   * Same payload as `getUpcomingEvents`, shared between the hero and the grid.
   * Both mount together on the home page, so without this they would issue the
   * same request twice on every load.
   */
  getUpcomingEventsShared(): Promise<UpcomingEventsResponse> {
    if (!sharedUpcomingRequest) {
      sharedUpcomingRequest = this.getUpcomingEvents().catch((error) => {
        sharedUpcomingRequest = null;
        throw error;
      });
    }

    return sharedUpcomingRequest;
  }

  /**
   * Fotos publicadas mais recentes, de todos os eventos ja encerrados.
   * O site devolve vitrine vazia quando as memorias estao desligadas ou quando
   * ha menos de tres fotos, entao a home simplesmente nao exibe a section.
   */
  async getRecentMemories(limit = 5): Promise<RecentMemoriesResponse> {
    const response = await this.fetchWithMultipleStrategies<RecentMemoriesResponse>(`/memories?limit=${limit}`);

    return {
      photos: Array.isArray(response?.photos) ? response.photos : [],
      total: Number(response?.total) || 0,
      events: Number(response?.events) || 0,
    };
  }

  /** Drops the shared payload so a retry hits the network again. */
  static clearUpcomingEventsCache(): void {
    sharedUpcomingRequest = null;
  }

  /**
   * Extract the numeric event id from an event URL
   * @param eventUrl Full event URL, e.g. `https://…/evento/3012_tropical/1113`
   * @returns Event id, or an empty string when the URL has no id
   */
  static extractEventIdFromUrl(eventUrl: string): string {
    const match = /\/evento\/[^/]+\/(\d+)\/?$/.exec(String(eventUrl || ''));

    return match ? match[1] : '';
  }

  /**
   * Extract the event name from the event URL
   * @param eventUrl Full event URL
   * @returns Cleaned event name
   */
  static extractEventNameFromUrl(eventUrl: string): string {
    try {
      const url = new URL(eventUrl);
      const pathParts = url.pathname.split('/');
      const eventSlug = pathParts[pathParts.length - 2] || pathParts[pathParts.length - 1];
      const eventName = eventSlug.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      return eventName
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 50);
    } catch {
      return 'Evento';
    }
  }

  /**
   * Parse date string into date and time
   * @param dateString Formatted string from API
   * @returns Object with `date` and `time`
   */
  static parseEventDate(dateString: string): { date: string; time: string } {
    try {
      const parts = dateString.split(' às ');
      const datePart = parts[0];
      const timePart = parts[1]?.replace('h', '') || '20:00';

      return {
        date: datePart,
        time: timePart,
      };
    } catch {
      return {
        date: dateString,
        time: '20:00',
      };
    }
  }

  /**
   * Clean up location string for display
   * @param location Raw location from API
   * @returns Cleaned location
   */
  static cleanLocation(location: string): string {
    return location.split(',')[0].trim();
  }

  /**
   * Perform a test fetch to check API connectivity
   * @returns True if API responds correctly
   */
  static async testApiConnectivity(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/events?type=highlights`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  static get isUsingLiveApi(): boolean {
    return true;
  }

  static get environmentInfo() {
    return {
      isUsingLiveApi: this.isUsingLiveApi,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
    };
  }
}

export const apiService = new ApiService();
export { ApiService };
